import React, { useContext, useEffect, useState } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../../auth/AuthProvider";
import { NavigationSources, PageSources, parentPageTitleMap } from "../../constants/constants";
import { useCompanyContext } from "../companies/useCompanyContext";
import { isSubscriptionsOffered } from "../companies/pdtiFeatureFlags";
import { Icon, Heading } from "../../designSystem";
import { checkIsFromApp } from "../utility/helpers";
import { isApproverReadOnly } from "./useApproverReadOnly";
import { resolvePageTitle } from "./usePageSectionTabs";

/** Nested settings routes, plus tax-rules (only 3 path segments) which still needs a back chevron. */
export const shouldShowNestedPageBackButton = (pathname: string): boolean => {
  const trimmed = pathname.replace(/^\/+|\/+$/g, "");
  const depth = trimmed.split("/").length;
  return depth > 3 || trimmed.endsWith("/tax-rules");
};

export const PageHeader: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    companyId,
    features,
    isAsicConnectionActivated,
    isFundRegistry,
    isLoadingCompanyInfo,
    registryType,
    userCompanyRoles,
  } = useCompanyContext();
  const subscriptionsOffered = isSubscriptionsOffered(features, registryType);
  const adminViewTitle = isApproverReadOnly(userCompanyRoles) ? "Approver view" : "Admin view";
  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<string>("");
  const [matchedPath, setMatchedPath] = useState<string>("");
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    setPageTitle(
      resolvePageTitle(
        location.pathname,
        isAsicConnectionActivated,
        !isLoadingCompanyInfo && isFundRegistry,
        subscriptionsOffered
      )
    );

    const pathKeys = Object.keys(parentPageTitleMap);
    let found = "";
    for (const pathKey of pathKeys) {
      if (matchPath(location.pathname, { path: pathKey, exact: true })) {
        found = pathKey;
        break;
      }
    }
    setMatchedPath(found);
  }, [isFundRegistry, isAsicConnectionActivated, isLoadingCompanyInfo, location.pathname, subscriptionsOffered]);

  useEffect(() => {
    setShowBackButton(false);

    if (shouldShowNestedPageBackButton(location.pathname)) {
      if (
        ["Investors", "Investor Relations"].includes(pageTitle) ||
        (currentUser?.isInvestor && ["Investor profiles", "Investments", "Announcements"].includes(pageTitle))
      ) {
        setShowBackButton(false);
      } else {
        setShowBackButton(true);
      }
    }
  }, [currentUser?.isInvestor, location.pathname, pageTitle]);

  const announcementsPagePath = currentUser?.isInvestor
    ? `/investor/company/${companyId}/investor-relations/announcement`
    : `/company/${companyId}/investor-relations/announcements`;

  const investorsPagePath = `/company/${companyId}/investors/all`;
  const investorProfilesPagePath = `/investor/company/${companyId}/investor-profiles/active`;
  const adminInvestorRequestsPagePath = `/company/${companyId}/investors/requests`;
  const isNavigatingFromApp = checkIsFromApp(history);

  return (
    <>
      {!!pageTitle.length && (
        <>
          {pageTitle === "Admin" ? (
            <AdminHeader title={adminViewTitle} />
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                {showBackButton && (
                  <button
                    type="button"
                    aria-label="Go back"
                    onClick={() => {
                      if (pageTitle === "Announcement") {
                        history.push(announcementsPagePath);
                      } else if (pageTitle === "Investor holdings" && matchedPath.includes("/investors/")) {
                        history.push(investorsPagePath);
                      } else if (
                        pageTitle === "Add investor" &&
                        matchedPath.includes("/investor-profiles/") &&
                        !isNavigatingFromApp
                      ) {
                        history.push(investorProfilesPagePath);
                      } else if (pageTitle === "Review request" && matchedPath.includes("/investors/review-request")) {
                        history.push(adminInvestorRequestsPagePath, {
                          base: NavigationSources.fromApp,
                          page: PageSources.adminReviewRequest,
                        });
                      } else {
                        history.goBack();
                      }
                    }}
                    className="u-text-strong u-cursor-pointer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: 5,
                      marginRight: 5,
                      border: "none",
                      background: "transparent",
                    }}
                  >
                    <Icon name="chevronLeft" size={24} />
                  </button>
                )}
                <Heading level={3} style={{ margin: 0, paddingLeft: showBackButton ? 0 : 24 }}>
                  {pageTitle}
                </Heading>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const AdminHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ marginBottom: 20 }}>
    <Heading level={3} style={{ margin: 0, paddingLeft: 24 }}>
      {title}
    </Heading>
  </div>
);
